import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../config/api';
import { useAuth } from '../App';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Heart,
  Share2,
  DollarSign,
  Eye,
  Users,
  Star,
  BookOpen,
  Lock,
  Clock,
  ChevronRight,
  MessageCircle,
  Flame,
  Laugh,
  Zap,
} from 'lucide-react';

const API = API_URL;

const SeriesDetail = () => {
  const { seriesId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [seriesData, setSeriesData] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [comments, setComments] = useState([]);
  const [relatedSeries, setRelatedSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribed, setSubscribed] = useState(false);
  const [episodeSort, setEpisodeSort] = useState('newest');
  const [newComment, setNewComment] = useState('');
  const [updatingEpisodeId, setUpdatingEpisodeId] = useState(null);
  const [deletingEpisodeId, setDeletingEpisodeId] = useState(null);
  const [showTipModal, setShowTipModal] = useState(false);

  // Dynamic page title based on series name
  useDocumentTitle(seriesData?.title);

  const fetchSeriesData = async () => {
    try {
      const [detailRes, episodesRes, commentsRes, relatedRes] = await Promise.all([
        axios.get(`${API}/series/${seriesId}/detail`),
        axios.get(`${API}/series/${seriesId}/chapters`),
        axios.get(`${API}/series/${seriesId}/comments?limit=10`),
        axios.get(`${API}/series/${seriesId}/related?limit=6`),
      ]);

      setSeriesData(detailRes.data);
      setEpisodes(episodesRes.data);
      setComments(commentsRes.data);
      setRelatedSeries(relatedRes.data);

      if (user) {
        try {
          const subRes = await axios.get(`${API}/series/${seriesId}/subscription-status`);
          setSubscribed(subRes.data.subscribed);
        } catch (err) {
          console.error('Error checking subscription:', err);
        }
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching series data:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (seriesId) {
      fetchSeriesData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesId]);

  const handleSubscribe = async () => {
    if (!user) {
      toast.error('Please login to subscribe');
      navigate('/auth');
      return;
    }

    try {
      if (subscribed) {
        await axios.delete(`${API}/series/${seriesId}/subscribe`);
        setSubscribed(false);
        toast.success('Unsubscribed successfully');
      } else {
        await axios.post(`${API}/series/${seriesId}/subscribe`);
        setSubscribed(true);
        toast.success('Subscribed successfully! 🎉');
      }
    } catch (error) {
      console.error('Error toggling subscription:', error);
      toast.error('Failed to update subscription');
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: seriesData?.series?.title,
        text: seriesData?.series?.description,
        url,
      });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };

  const handleAddComment = async () => {
    if (!user) {
      toast.error('Please login to comment');
      navigate('/auth');
      return;
    }
    if (!newComment.trim()) {
      toast.error('Comment cannot be empty');
      return;
    }
    try {
      const response = await axios.post(`${API}/series/${seriesId}/comments`, {
        content: newComment,
      });
      setComments([response.data, ...comments]);
      setNewComment('');
      toast.success('Comment posted!');
    } catch (error) {
      console.error('Error posting comment:', error);
      toast.error('Failed to post comment');
    }
  };

  const handleTogglePublish = async (episode) => {
    if (!user) {
      toast.error('Please login to manage episodes');
      navigate('/auth');
      return;
    }

    const currentlyPublished = episode.is_published === 'published';
    const nextStatus = currentlyPublished ? 'unpublished' : 'published';

    setUpdatingEpisodeId(episode.id);
    try {
      const response = await axios.patch(`${API}/series/${seriesId}/chapters/${episode.id}/status`, {
        is_published: nextStatus,
      });
      const updated = response.data;
      setEpisodes((prev) => prev.map((ep) => (ep.id === episode.id ? { ...ep, ...updated } : ep)));
      toast.success(currentlyPublished ? 'Episode unpublished' : 'Episode published');
    } catch (error) {
      console.error('Error updating episode status:', error);
      const detail = error.response?.data?.detail || 'Failed to update episode status';
      toast.error(detail);
    } finally {
      setUpdatingEpisodeId(null);
    }
  };

  const handleDeleteEpisode = async (episode) => {
    if (!user) {
      toast.error('Please login to manage episodes');
      navigate('/auth');
      return;
    }

    const confirmed = window.confirm(
      `Delete Episode ${episode.chapter_number}: ${episode.title}? This cannot be undone.`,
    );
    if (!confirmed) return;

    setDeletingEpisodeId(episode.id);
    try {
      await axios.delete(`${API}/series/${seriesId}/chapters/${episode.id}`);
      setEpisodes((prev) => prev.filter((ep) => ep.id !== episode.id));
      toast.success('Episode deleted');
    } catch (error) {
      console.error('Error deleting episode:', error);
      const detail = error.response?.data?.detail || 'Failed to delete episode';
      toast.error(detail);
    } finally {
      setDeletingEpisodeId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4" />
          <div className="text-xl font-medium">Loading series...</div>
        </div>
      </div>
    );
  }

  if (!seriesData) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Series not found</h2>
          <Link to="/">
            <Button className="bg-orange-500 hover:bg-orange-600">Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const { series, creator, stats, owner_user_id } = seriesData;
  const isOwner =
    user &&
    owner_user_id &&
    owner_user_id === user.id &&
    !(user.role === 'admin' || user.is_admin === true || user.is_admin === 'true');

  const sortedEpisodes = [...episodes].sort((a, b) => {
    const aNum = Number(a.chapter_number) || 0;
    const bNum = Number(b.chapter_number) || 0;
    return episodeSort === 'newest' ? bNum - aNum : aNum - bNum;
  });

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Breadcrumb */}
      <div className="bg-gray-900 border-b border-gray-800">
        <div className="container mx-auto px-6 py-3">
          <div className="flex items-center space-x-2 text-sm text-gray-400">
            <Link to="/" className="hover:text-white">
              Home
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to={`/?category=${series.genres?.[0]}`} className="hover:text-white">
              {series.genres?.[0] || 'Comics'}
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">{series.title}</span>
          </div>
        </div>
      </div>

      {/* Header Section with Blurred Background */}
      <div className="relative">
        {/* Blurred/hero background */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: series.billboard_url
              ? `url('${series.billboard_url}')`
              : series.cover_url
                ? `url('${series.cover_url}')`
                : 'linear-gradient(to bottom right, #111827, #000000)',
            filter: series.billboard_url ? 'none' : 'blur(20px)',
            transform: 'scale(1.1)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-black" />

        <div className="relative container mx-auto px-6 py-12">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Cover Image (2:3 portrait, contain) */}
            <div className="flex-shrink-0">
              <div className="w-64 h-96 bg-black/60 rounded-lg shadow-2xl border-2 border-orange-500 flex items-center justify-center overflow-hidden">
                <img
                  src={series.cover_url}
                  alt={series.title}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    const target = e.currentTarget;
                    target.onerror = null;
                    target.style.display = 'none';
                    const fallback = document.createElement('div');
                    fallback.className = 'w-full h-full flex items-center justify-center text-xs text-gray-400 bg-black/50';
                    fallback.textContent = 'Cover image missing – please reupload';
                    target.parentNode?.appendChild(fallback);
                  }}
                />
              </div>
            </div>

            {/* Series Info */}
            <div className="flex-1">
              <h1 className="text-4xl lg:text-5xl font-black mb-4">{series.title}</h1>

              <div className="flex flex-wrap gap-2 mb-4">
                {series.genres?.map((genre) => (
                  <Badge key={genre} className="bg-orange-500 text-white">
                    {genre}
                  </Badge>
                ))}
                <Badge
                  className={`${
                    series.status === 'completed' ? 'bg-green-500' : 'bg-blue-500'
                  } text-white`}
                >
                  {series.status === 'completed' ? 'Completed' : 'Ongoing'}
                </Badge>
              </div>

              <div className="flex flex-wrap gap-6 mb-6 text-gray-300">
                <div className="flex items-center space-x-2">
                  <Eye className="w-5 h-5 text-orange-400" />
                  <span>{stats.views.toLocaleString()} views</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Users className="w-5 h-5 text-orange-400" />
                  <span>{stats.subscribers.toLocaleString()} subscribers</span>
                </div>
                <div className="flex items-center space-x-2">
                  <BookOpen className="w-5 h-5 text-orange-400" />
                  <span>{stats.chapters} episodes</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  <span>{stats.rating}/10</span>
                </div>
              </div>

              <Link
                to={
                  creator.profile?.handle
                    ? `/creator/${creator.profile.handle}`
                    : `/creator/${creator.profile?.id}`
                }
                className="flex items-center space-x-3 mb-6 p-3 bg-black/50 rounded-lg hover:bg-black/70 transition-colors w-fit"
              >
                <Avatar className="w-12 h-12 border-2 border-orange-500">
                  <AvatarImage src={creator.profile?.avatar_url} />
                  <AvatarFallback className="bg-orange-500 text-white">
                    {creator.profile?.display_name?.slice(0, 2).toUpperCase() || 'CR'}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-white font-medium">{creator.profile?.display_name}</p>
                  <p className="text-gray-400 text-sm">@{creator.profile?.handle}</p>
                </div>
              </Link>

              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={handleSubscribe}
                  className={`${
                    subscribed
                      ? 'bg-gray-700 hover:bg-gray-600'
                      : 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600'
                  } text-white font-bold`}
                >
                  <Heart className={`w-4 h-4 mr-2 ${subscribed ? 'fill-white' : ''}`} />
                  {subscribed ? 'Subscribed' : 'Subscribe'}
                </Button>

                <Dialog open={showTipModal} onOpenChange={setShowTipModal}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-white"
                    >
                      <DollarSign className="w-4 h-4 mr-2" />
                      Tip Creator
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-gray-900 border-gray-700 text-white">
                    <DialogHeader>
                      <DialogTitle>Support {creator.profile?.display_name}</DialogTitle>
                      <DialogDescription className="text-gray-400">
                        Show your appreciation with a tip!
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                      <Button className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold">
                        $2 - Small Tip
                      </Button>
                      <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold">
                        $5 - Medium Tip
                      </Button>
                      <Button className="w-full bg-red-500 hover:bg-red-600 text-white font-bold">
                        $10 - Large Tip
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                <Button
                  onClick={handleShare}
                  variant="outline"
                  className="border-gray-600 text-gray-300 hover:bg-gray-800"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Episodes */}
          <div className="lg:col-span-2 space-y-8">
            <Card className="bg-gray-900 border-gray-800">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold mb-4">About</h2>
                <p className="text-gray-300 leading-relaxed mb-6">{series.description}</p>
                <div className="flex flex-wrap gap-3">
                  {episodes.length > 0 && (
                    <>
                      <Link to={`/series/${seriesId}/read/${episodes[0].id}`}>
                        <Button className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold">
                          <BookOpen className="w-4 h-4 mr-2" />
                          Read First Episode
                        </Button>
                      </Link>
                      {episodes.length > 1 && (
                        <Link to={`/series/${seriesId}/read/${episodes[episodes.length - 1].id}`}>
                          <Button
                            variant="outline"
                            className="border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-white"
                          >
                            <Zap className="w-4 h-4 mr-2" />
                            Read Latest
                          </Button>
                        </Link>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Episode List */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">Episodes</h2>
                <Select value={episodeSort} onValueChange={setEpisodeSort}>
                  <SelectTrigger className="w-40 bg-gray-900 border-gray-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700 text-white">
                    <SelectItem value="newest">Newest First</SelectItem>
                    <SelectItem value="oldest">Oldest First</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                {sortedEpisodes.map((episode) => {
                  const publishStatus = episode.is_published;
                  const statusLabel =
                    publishStatus === 'published'
                      ? 'Published'
                      : publishStatus === 'draft'
                        ? 'Draft'
                        : 'Unpublished';
                  const statusColor =
                    publishStatus === 'published'
                      ? 'bg-green-600 text-white'
                      : publishStatus === 'draft'
                        ? 'bg-yellow-600 text-white'
                        : 'bg-gray-700 text-gray-200';

                  return (
                    <Card
                      key={episode.id}
                      className="bg-gray-900 border-gray-800 hover:border-orange-500 transition-all duration-300 group"
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center space-x-4">
                          <div className="w-20 h-20 bg-gray-800 rounded-lg flex-shrink-0 flex items-center justify-center group-hover:bg-gray-700 transition-colors">
                            <span className="text-2xl font-bold text-orange-500">
                              {episode.chapter_number}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center space-x-2 min-w-0">
                                <Link
                                  to={`/series/${seriesId}/read/${episode.id}`}
                                  className="hover:text-orange-400 truncate"
                                >
                                  <h3 className="text-white font-bold truncate">
                                    Episode {episode.chapter_number}: {episode.title}
                                  </h3>
                                </Link>
                                {episode.is_free === 'false' && (
                                  <Lock className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                                )}
                              </div>
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColor}`}
                              >
                                {statusLabel}
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-sm text-gray-400">
                              <div className="flex items-center space-x-4">
                                <span className="flex items-center">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {episode.created_at
                                    ? new Date(episode.created_at).toLocaleDateString()
                                    : '—'}
                                </span>
                                <span className="flex items-center">
                                  <Eye className="w-3 h-3 mr-1" />
                                  {episode.metrics_cache?.views || 0} views
                                </span>
                              </div>
                              {isOwner && (
                                <div className="flex items-center space-x-2">
                                  <Link
                                    to={`/series/${seriesId}/upload?episodeId=${episode.id}`}
                                    className="text-xs text-gray-300 hover:text-white underline"
                                  >
                                    Edit
                                  </Link>
                                  <Link
                                    to={`/series/${seriesId}/episode/${episode.id}/pages`}
                                    className="text-xs text-gray-300 hover:text-white underline"
                                  >
                                    Manage Pages
                                  </Link>
                                  <button
                                    type="button"
                                    onClick={() => handleTogglePublish(episode)}
                                    disabled={
                                      updatingEpisodeId === episode.id || deletingEpisodeId === episode.id
                                    }
                                    className="text-xs px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-gray-200 border border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {updatingEpisodeId === episode.id
                                      ? 'Saving...'
                                      : episode.is_published === 'published'
                                        ? 'Unpublish'
                                        : 'Republish'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteEpisode(episode)}
                                    disabled={
                                      deletingEpisodeId === episode.id || updatingEpisodeId === episode.id
                                    }
                                    className="text-xs px-2 py-1 rounded bg-red-900 hover:bg-red-800 text-red-200 border border-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {deletingEpisodeId === episode.id ? 'Deleting...' : 'Delete'}
                                  </button>
                                </div>
                              )}
                              {!isOwner && (
                                <ChevronRight className="w-6 h-6 text-gray-600 group-hover:text-orange-500 transition-colors flex-shrink-0" />
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}

                {episodes.length === 0 && (
                  <Card className="bg-gray-900 border-gray-800">
                    <CardContent className="p-12 text-center">
                      <BookOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-400">No episodes available yet.</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>

            {/* Comments Section */}
            <div>
              <h2 className="text-2xl font-bold mb-6 flex items-center">
                <MessageCircle className="w-6 h-6 mr-2 text-orange-400" />
                Comments ({comments.length})
              </h2>

              {user ? (
                <Card className="bg-gray-900 border-gray-800 mb-6">
                  <CardContent className="p-4">
                    <Textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Share your thoughts..."
                      className="bg-gray-800 border-gray-700 text-white mb-3"
                      rows={3}
                    />
                    <Button
                      onClick={handleAddComment}
                      className="bg-orange-500 hover:bg-orange-600"
                    >
                      Post Comment
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-gray-900 border-gray-800 mb-6">
                  <CardContent className="p-6 text-center">
                    <p className="text-gray-400 mb-3">Login to leave a comment</p>
                    <Link to="/auth">
                      <Button className="bg-orange-500 hover:bg-orange-600">Login</Button>
                    </Link>
                  </CardContent>
                </Card>
              )}

              <div className="space-y-4">
                {comments.map((comment) => (
                  <Card key={comment.id} className="bg-gray-900 border-gray-800">
                    <CardContent className="p-4">
                      <div className="flex items-start space-x-3">
                        <Avatar className="w-10 h-10">
                          <AvatarFallback className="bg-orange-500 text-white">
                            {comment.username?.slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="text-white font-medium">{comment.username}</span>
                            <span className="text-gray-500 text-sm">
                              {new Date(comment.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-gray-300 mb-3">{comment.content}</p>
                          <div className="flex items-center space-x-4 text-sm">
                            <button className="flex items-center space-x-1 text-gray-400 hover:text-orange-400">
                              <Flame className="w-4 h-4" />
                              <span>{comment.reactions?.fire || 0}</span>
                            </button>
                            <button className="flex items-center space-x-1 text-gray-400 hover:text-red-400">
                              <Heart className="w-4 h-4" />
                              <span>{comment.reactions?.heart || 0}</span>
                            </button>
                            <button className="flex items-center space-x-1 text-gray-400 hover:text-yellow-400">
                              <Laugh className="w-4 h-4" />
                              <span>{comment.reactions?.laugh || 0}</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {comments.length === 0 && (
                  <Card className="bg-gray-900 border-gray-800">
                    <CardContent className="p-12 text-center">
                      <MessageCircle className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-400">No comments yet. Be the first!</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Creator Panel & Related */}
          <div className="space-y-6">
            <Card className="bg-gray-900 border-gray-800">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-4">About the Creator</h3>
                <Link
                  to={
                    creator.profile?.handle
                      ? `/creator/${creator.profile.handle}`
                      : `/creator/${creator.profile?.id}`
                  }
                  className="block mb-4"
                >
                  <div className="flex items-center space-x-3 p-3 bg-black/50 rounded-lg hover:bg-black/70 transition-colors">
                    <Avatar className="w-16 h-16 border-2 border-orange-500">
                      <AvatarImage src={creator.profile?.avatar_url} />
                      <AvatarFallback className="bg-orange-500 text-white text-xl">
                        {creator.profile?.display_name?.slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="text-white font-bold">{creator.profile?.display_name}</p>
                      <p className="text-gray-400 text-sm">@{creator.profile?.handle}</p>
                    </div>
                  </div>
                </Link>

                <p className="text-gray-300 text-sm mb-4 line-clamp-3">
                  {creator.profile?.bio_richtext}
                </p>

                <div className="space-y-2">
                  <Link
                    to={
                      creator.profile?.handle
                        ? `/creator/${creator.profile.handle}`
                        : `/creator/${creator.profile?.id}`
                    }
                  >
                    <Button
                      variant="outline"
                      className="w-full border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-white"
                    >
                      View Profile
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    className="w-full border-gray-600 text-gray-300 hover:bg-gray-800"
                  >
                    More by This Creator
                  </Button>
                </div>
              </CardContent>
            </Card>

            {relatedSeries.length > 0 && (
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-4">You May Also Like</h3>
                  <div className="space-y-3">
                    {relatedSeries.map((related) => (
                      <Link
                        key={related.id}
                        to={`/series/${related.id}`}
                        className="block group"
                      >
                        <div className="flex items-center space-x-3 p-2 rounded-lg hover:bg-black/50 transition-colors">
                          <img
                            src={related.cover_url}
                            alt={related.title}
                            className="w-16 h-20 object-cover rounded"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium truncate group-hover:text-orange-400">
                              {related.title}
                            </p>
                            <p className="text-gray-400 text-xs truncate">
                              {related.genres?.join(', ')}
                            </p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeriesDetail;
